// src/modules/activity/components/ActivityView/index.js
import React, { useState } from 'react';
import { Card, Button, Image, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { formatDuration, calculatePace } from '../../../../shared/utils/formatters';
import Alert from '../../../../shared/components/Alert';
import RegisterActivityForm from '../RegisterActivityForm';
import { useAuth } from '../../../../shared/hooks/useAuth';
import './styles.css';

const ACTIVITY_ICON_MAP = {
    running: 'bi-person-walking',
    cycling: 'bi-bicycle',
    swimming: 'bi-water',
    hiking: 'bi-tree',
    walking: 'bi-person-walking',
    football: 'bi-trophy',
    badminton: 'bi-circle',
    other: 'bi-activity'
};

const ACTIVITY_COLOR_CLASS = {
    running: 'text-danger',
    cycling: 'text-primary',
    swimming: 'text-info',
    hiking: 'text-success',
    walking: 'text-secondary',
    football: 'text-warning',
    badminton: 'text-primary',
    other: 'text-muted'
};

const getActivityIcon = (type) => ACTIVITY_ICON_MAP[type?.toLowerCase()] || 'bi-activity';
const getActivityColorClass = (type) => ACTIVITY_COLOR_CLASS[type?.toLowerCase()] || 'text-muted';

const ActivityView = ({
    activity,
    activityData,
    attachments,
    onEdit,
    onDelete,
    handleChange,
    handleFileChange,
    handleRemoveAttachment,
    alert,
    showAlert,
    onAlertClose,
    isLoading,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const { user } = useAuth();

    const canModify = user?.Username === activity.username;
    const activityDate = new Date(activity.date);
    const activityType = activity.activityType?.charAt(0).toUpperCase() + activity.activityType?.slice(1);
    const iconClass = getActivityIcon(activity.activityType);
    const colorClass = getActivityColorClass(activity.activityType);

    return (
        <>
            <Card className="activity-detail-card">
                <Card.Header className="activity-detail-header">
                    <div className="activity-detail-user">
                        <Link to={`/profile/${activity.username}`} className="activity-detail-avatar-link">
                            {activity.profilePicture ? (
                                <Image
                                    src={activity.profilePicture}
                                    roundedCircle
                                    className="activity-detail-avatar"
                                    alt={`${activity.firstName} ${activity.lastName}`}
                                />
                            ) : (
                                <div className="activity-detail-avatar-placeholder">
                                    <i className="bi bi-person"></i>
                                </div>
                            )}
                        </Link>
                        <div className="activity-detail-user-info">
                            <Link to={`/profile/${activity.username}`} className="activity-detail-username">
                                {activity.firstName} {activity.lastName}
                            </Link>
                            <span className="activity-detail-type">
                                <i className={`bi ${iconClass} ${colorClass} me-1`}></i>
                                {activityType}
                            </span>
                        </div>
                    </div>

                    <div className="activity-detail-header-right">
                        <div className="activity-detail-points">
                            <span className="points-value">{activity.points.toFixed(1)}</span>
                            <span className="points-label">points</span>
                        </div>
                        {canModify && !isEditing && (
                            <div className="action-buttons">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <i className="bi bi-pencil me-1"></i>Edit
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    <i className="bi bi-trash me-1"></i>Delete
                                </Button>
                            </div>
                        )}
                        {isEditing && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </Card.Header>

                <Card.Body>
                    {alert.show && showAlert && (
                        <Alert
                            variant={alert.variant}
                            message={alert.message}
                            show={alert.show}
                            onClose={onAlertClose}
                        />
                    )}

                    {isEditing ? (
                        <RegisterActivityForm
                            activityData={activityData}
                            attachments={attachments}
                            handleChange={handleChange}
                            handleSubmit={(e) => {
                                onEdit(e);
                                setIsEditing(false);
                            }}
                            handleFileChange={handleFileChange}
                            handleRemoveAttachment={handleRemoveAttachment}
                            isLoading={isLoading}
                            alert={alert}
                            showAlert={showAlert}
                            isEditing={true}
                        />
                    ) : (
                        <>
                            <div className="activity-detail-date">
                                <i className="bi bi-calendar3 me-1"></i>
                                <span>
                                    {activityDate.toLocaleDateString(undefined, {
                                        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                                    })}
                                </span>
                                <span className="ms-3">
                                    <i className="bi bi-clock me-1"></i>
                                    {activityDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>

                            <div className="activity-detail-stats">
                                <div className="activity-stat-item">
                                    <i className="bi bi-geo-alt"></i>
                                    <span className="stat-value">{activity.distance.toFixed(2)} <span className="stat-unit">km</span></span>
                                    <span className="stat-label">Distance</span>
                                </div>
                                <div className="activity-stat-item">
                                    <i className="bi bi-clock"></i>
                                    <span className="stat-value">{formatDuration(activity.duration)}</span>
                                    <span className="stat-label">Duration</span>
                                </div>
                                <div className="activity-stat-item">
                                    <i className="bi bi-speedometer2"></i>
                                    <span className="stat-value">{calculatePace(activity.duration, activity.distance, activity.activityType)}</span>
                                    <span className="stat-label">Pace</span>
                                </div>
                                {activity.elevation > 0 && (
                                    <div className="activity-stat-item">
                                        <i className="bi bi-arrow-up-right"></i>
                                        <span className="stat-value">{activity.elevation} <span className="stat-unit">m</span></span>
                                        <span className="stat-label">Elevation</span>
                                    </div>
                                )}
                                {activity.calories > 0 && (
                                    <div className="activity-stat-item">
                                        <i className="bi bi-fire"></i>
                                        <span className="stat-value">{activity.calories} <span className="stat-unit">kcal</span></span>
                                        <span className="stat-label">Calories</span>
                                    </div>
                                )}
                            </div>

                            {activity.description && (
                                <p className="activity-detail-description">{activity.description}</p>
                            )}

                            {activity.attachments?.length > 0 && (
                                <div className="activity-detail-images">
                                    {activity.attachments.map((attachment, index) => (
                                        <div
                                            key={index}
                                            className="activity-detail-thumbnail"
                                            onClick={() => setEnlargedImage(attachment.url)}
                                        >
                                            <Image
                                                src={attachment.url}
                                                alt={`Activity attachment ${index + 1}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this activity? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={onDelete}>
                        Delete Activity
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!enlargedImage} onHide={() => setEnlargedImage(null)} centered size="lg">
                <Modal.Body className="p-0 d-flex justify-content-center align-items-center">
                    {enlargedImage && (
                        <Image
                            src={enlargedImage}
                            alt="Enlarged attachment"
                            fluid
                            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

ActivityView.propTypes = {
    activity: PropTypes.shape({
        activityType: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        distance: PropTypes.number.isRequired,
        calories: PropTypes.number.isRequired,
        duration: PropTypes.number.isRequired,
        points: PropTypes.number.isRequired,
        elevation: PropTypes.number.isRequired,
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        profilePicture: PropTypes.string,
        description: PropTypes.string,
        attachments: PropTypes.arrayOf(PropTypes.shape({
            url: PropTypes.string.isRequired
        }))
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleFileChange: PropTypes.func.isRequired,
    handleRemoveAttachment: PropTypes.func.isRequired,
    alert: PropTypes.shape({
        show: PropTypes.bool.isRequired,
        variant: PropTypes.string.isRequired,
        message: PropTypes.string.isRequired
    }).isRequired,
    showAlert: PropTypes.bool.isRequired,
    activityData: PropTypes.shape({
        type: PropTypes.string.isRequired,
        distance: PropTypes.string.isRequired,
        calories: PropTypes.number.isRequired,
        elevation: PropTypes.string.isRequired,
        duration: PropTypes.string.isRequired,
        date: PropTypes.instanceOf(Date).isRequired,
        attachments: PropTypes.array.isRequired
    }).isRequired,
    attachments: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
};

export default ActivityView;
